import PropTypes from 'prop-types';
import React from 'react';
import { Map } from 'immutable';
import _ from 'lodash';
import Col from 'react-bootstrap/lib/Col';
import Row from 'react-bootstrap/lib/Row';
import { InView } from 'react-intersection-observer';

import BaseModal, { baseModalScrollTo } from "../general/BaseModal";
import { GenericModals } from '../../constants/AppConstants';
import TrackingActions from "../../actions/TrackingActions";
import t from "../../lib/I18n";
import trainTime from '../../lib/TrainTime';

import ValidatedFormField from '../form/ValidatedField';
import connectToStores from '../../helpers/connectToStores';
import Railcard1617Store from '../../stores/Railcard1617Store';
import UserStore from '../../stores/UserStore';
import CustomerActions from '../../actions/CustomerActions';
import Railcard1617Actions from '../../actions/Railcard1617Actions';
import Railcard1617Form from '../../records/search/Railcard1617Form';

import FeatureFlag from '../general/FeatureFlag';
import Loading from '../general/Loading';
import FormattedHTMLMessage from '../general/FormattedHTMLMessage';
import BAlert, { bAlertType, VisibleBAlert } from '../general/BAlert';
import Checkbox from '../form/Checkbox';
import FormComponent from "../form/FormComponent";
import DateSelector from '../search/dates/DateSelector';


const getStateFromStores = () => ({
    form: Railcard1617Store.getForm(),
    guestRailcardErrors: Railcard1617Store.getGuestRailcardErrors(),
    isSubmitting: Railcard1617Store.get('submitting'),
    user: UserStore.getCurrent(),
    validationSuccess: Railcard1617Store.get('validationSuccess'),
});

export class Railcard1617Modal extends FormComponent {
    static propTypes = {
        form: PropTypes.instanceOf(Railcard1617Form),
        guestRailcardErrors: PropTypes.instanceOf(Map),
        isSubmitting: PropTypes.bool,
        validationSucess: PropTypes.bool,
        onClose: PropTypes.func.isRequired,
        onContinue: PropTypes.func.isRequired,
        disableSuccessModal: PropTypes.bool,
    };

    state = {
        formInvalid: false,
        submitted: false,
        isLoading: false
    };

    _handleFormChange = (field, value) => {
        CustomerActions.changeRailcard1617Form(field, value);

        this.setState({
            formInvalid: false,
            submitted: false
        });
    };

    componentDidUpdate({ validationSuccess: prevValidationSuccess }) {
        const { form, user, validationSuccess, disableSuccessModal } = this.props;

        const railcardData = {
            railcardNumber: form.railcardNumber,
            firstName: form.firstName,
            lastName: form.lastName,
            sync: form.syncKeyGo,
            expiryDate: form.expiryDate,
            user
        };

        if (validationSuccess && !prevValidationSuccess) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ isLoading: true });

            _.defer(async () => {
                const result = await Railcard1617Actions.linkToAccount(railcardData);

                if (result && disableSuccessModal) {
                    _.defer(this._handleClose);
                }
            });
        }
    }

    _handleClose = () => {
        Railcard1617Actions.resetValidation();
        Railcard1617Actions.flashCheckbox(true);
        setTimeout(() => Railcard1617Actions.flashCheckbox(false), 3000); // railcard1617.less animation-duration
        this.props.onClose();
    }

    _handleContinue = () => {
        const { onContinue, form } = this.props;

        this.setState({ submitted: true });

        if (form.isComplete()) {
            if (typeof onContinue === 'function') {
                onContinue({
                    railcardNumber: form.railcardNumber,
                    firstName: form.firstName,
                    lastName: form.lastName,
                    expiryDate: form.expiryDate,
                });
            }
        }
    }

    _handleToggleFor = (name) => (event) => {
        const { checked } = event.target;

        this._handleFormChange(name, checked);
    };

    _onInViewChange = (inView, entry) => {
        if (!inView) {
            const top = Math.min(entry.boundingClientRect.bottom - entry.boundingClientRect.top / 2, entry.boundingClientRect.top + 50);
            baseModalScrollTo({ top });
        }
    }

    _renderSyncKeyGoLabel = () => (<React.Fragment>
        <FormattedHTMLMessage message={ t('booking.railcard1617.keyGoLinkingLabel', { email: this.props.user.email }) } />
        { this.props.form.get('syncKeyGo') && <InView triggerOnce as="div" onChange={ this._onInViewChange } threshold="0.9">
                <FormattedHTMLMessage message={ t('booking.railcard1617.keyGoLinkingWarning') } />
            </InView> }
    </React.Fragment>);

    render() {
        const {
            form,
            guestRailcardErrors,
            isSubmitting,
            user,
            validationSuccess,
        } = this.props;
        const { submitted } = this.state;

        const modalProps = {
            className: 'modal--sm',
            continueText: 'Confirm',
            disableContinue: isSubmitting,
            hideContinue: validationSuccess,
            onClose: this._handleClose,
            onContinue: this._handleContinue,
            titleText: t('booking.railcards.season.railcard1617.heading'),
            onOpen: () => {
                TrackingActions.genericModalOpen(GenericModals.RAILCARD1617);
            }
        };

        return (
            <BaseModal { ...modalProps }>
                <div className="margin-bottom--sm">
                    {guestRailcardErrors.size > 0 ?
                        <VisibleBAlert className="railcard1617-modal__alert" type={ bAlertType.DANGER }>
                            {t(guestRailcardErrors.first())}
                        </VisibleBAlert> : null}
                    {(!this.state.isLoading && validationSuccess)
                        ? <BAlert className="railcard1617-modal__alert" type={ bAlertType.SUCCESS }>{t('booking.railcard1617.validationSuccess')}</BAlert>
                        : <form className={ isSubmitting ? 'railcard1617-modal--loading' : null }>
                            <p>{t('booking.railcard1617.enterDetails')}</p>
                            <h2 className="label">{t('booking.railcard1617.railcardDetails')}</h2>
                            <Row>
                                <Col xs={ 12 }>
                                    <ValidatedFormField { ...this.props }
                                        form={ form }
                                        type="text"
                                        id="firstName"
                                        name="firstName"
                                        field="firstName"
                                        label={ t('booking.railcard1617.form.firstName') }
                                        visited={ submitted }
                                        onChange={ this._handleFormChange }
                                        placeholder={ `${t('booking.railcard1617.form.firstName')}...` }
                                        labelClassName="sr-only"
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={ 12 }>
                                    <ValidatedFormField { ...this.props }
                                        form={ form }
                                        type="text"
                                        id="lastName"
                                        name="lastName"
                                        field="lastName"
                                        label={ t('booking.railcard1617.form.surname') }
                                        visited={ submitted }
                                        onChange={ this._handleFormChange }
                                        placeholder={ `${t('booking.railcard1617.form.surname')}...` }
                                        labelClassName="sr-only"
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={ 12 }>
                                    <ValidatedFormField { ...this.props }
                                        form={ form }
                                        type="text"
                                        id="railcardNumber"
                                        name="railcardNumber"
                                        field="railcardNumber"
                                        label={ t('booking.railcard1617.form.railcardNumber') }
                                        visited={ submitted }
                                        onChange={ this._handleFormChange }
                                        placeholder={ `${t('booking.railcard1617.form.railcardNumber')}...` }
                                        labelClassName="sr-only"
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={ 12 }>
                                    <span className="label">{ t('railcard.add.expiry-date') }</span><br />
                                    <DateSelector
                                        className="add-railcard-form__expiry-date"
                                        datetime={ form.get('expiryDate') }
                                        datetimeFormats={ {
                                            sameDay: '[Today]',
                                            nextDay: '[Tomorrow]',
                                            nextWeek: 'DD-MM-YYYY',
                                            sameElse: 'DD-MM-YYYY'
                                        } }
                                        min={ trainTime() }
                                        max={ trainTime().add(3, 'years') }
                                        onChange={ (value) => this._handleFormChange('expiryDate', value) }
                                        required
                                    />
                                </Col>
                            </Row>
                            { user.isLoggedIn() &&
                              <FeatureFlag name="enableKeyGo">
                                <Row>
                                    <Col xs={12}>
                                        <Checkbox
                                            type="checkbox"
                                            name="syncKeyGo"
                                            label={this._renderSyncKeyGoLabel()}
                                            value
                                            checked={form.get('syncKeyGo')}
                                            onChange={this._handleToggleFor('syncKeyGo')}
                                        />
                                    </Col>
                                </Row>
                            </FeatureFlag>
                        }
                        </form>}
                    {isSubmitting && <Loading unwrapped className="loading--centered" />}
                </div>
            </BaseModal>
        );
    }
}

export default connectToStores([Railcard1617Store, UserStore], getStateFromStores)(Railcard1617Modal);



// WEBPACK FOOTER //
// ./src/components/seasonTicket/Railcard1617Modal.js